export interface PlaybackPosition {
  measureIndex: number;
  beatIndex: number;
  beatProgress: number;
}

export class PracticeAudioEngine {
  readonly audio = new Audio();

  private context?: AudioContext;
  private source?: MediaElementAudioSourceNode;
  private splitter?: ChannelSplitterNode;

  private pianoGain?: GainNode;
  private clickGain?: GainNode;
  private merger?: ChannelMergerNode;

  private totalBeats = 0;
  private secondsPerBeat = 0;

  constructor(src: string, totalMeasures: number) {
    this.totalBeats = totalMeasures * 4;

    this.audio.crossOrigin = 'anonymous';
    this.audio.src = src;
    this.audio.loop = true;
    this.audio.preload = 'auto';
  }

  public async initialize(): Promise<void> {
    if (this.context) {
      return;
    }

    this.context = new AudioContext();

    this.source = this.context.createMediaElementSource(this.audio);
    this.splitter = this.context.createChannelSplitter(2);

    this.pianoGain = this.context.createGain();
    this.clickGain = this.context.createGain();

    this.merger = this.context.createChannelMerger(2);

    this.source.connect(this.splitter);

    // Left channel: piano
    this.splitter.connect(this.pianoGain, 0);

    // Right channel: metronome
    this.splitter.connect(this.clickGain, 1);

    // Send piano to both output channels.
    this.pianoGain.connect(this.merger, 0, 0);
    this.pianoGain.connect(this.merger, 0, 1);

    // Send click to both output channels.
    this.clickGain.connect(this.merger, 0, 0);
    this.clickGain.connect(this.merger, 0, 1);

    this.merger.connect(this.context.destination);

    this.clickGain.gain.value = 1;

    await this.waitForMetadata();

    this.secondsPerBeat = this.audio.duration / this.totalBeats;
  }

  async play(): Promise<void> {
    await this.initialize();

    await this.context?.resume();
    await this.audio.play();
  }

  pause(): void {
    this.audio.pause();
  }

  setClickEnabled(enabled: boolean): void {
    if (!this.context || !this.clickGain) {
      return;
    }

    this.clickGain.gain.setTargetAtTime(
      enabled ? 1 : 0,
      this.context.currentTime,
      0.01,
    );
  }

  seekToMeasure(measureIndex: number): void {
    if (!this.secondsPerBeat) {
      return;
    }

    this.audio.currentTime = measureIndex * 4 * this.secondsPerBeat;
  }

  getPosition(): PlaybackPosition {
    if (!this.secondsPerBeat) {
      return {
        measureIndex: 0,
        beatIndex: 0,
        beatProgress: 0,
      };
    }

    const exactBeat = this.audio.currentTime / this.secondsPerBeat;
    const wrappedBeat = exactBeat % this.totalBeats;
    const beatNumber = Math.floor(wrappedBeat);

    return {
      measureIndex: Math.floor(beatNumber / 4),
      beatIndex: beatNumber % 4,
      beatProgress: wrappedBeat - beatNumber,
    };
  }

  private waitForMetadata(): Promise<void> {
    if (Number.isFinite(this.audio.duration)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.audio.addEventListener('loadedmetadata', () => resolve(), {
        once: true,
      });

      this.audio.addEventListener(
        'error',
        () => reject(new Error('The audio file could not be loaded.')),
        { once: true },
      );
    });
  }
}
